import { Search, Mail, Plus, Trash2, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react'

// ────────────────────────────────────────────────────────────────────────
// /brand — internal preview of the design system.
// Single page reference for tokens, typography, and component classes.
// Not linked from the nav; reach it directly via the URL.
// ────────────────────────────────────────────────────────────────────────

type Ramp = Record<string | number, string>

const BRAND: Ramp = {
  50:  '#ECFAF2', 100: '#CCF1DE', 200: '#95E0BC', 300: '#5BC894', 400: '#2DAA70',
  500: '#138A55', 600: '#0C6D43', 700: '#095636', 800: '#07402A', 900: '#052B1D',
}
const ACCENT: Ramp = {
  50:  '#FBF6E8', 100: '#F4E5B5', 200: '#EAD485', 300: '#E2C264', 400: '#D5AC44',
  500: '#C99A2D', 600: '#A87C16', 700: '#875F0E', 800: '#6A4909', 900: '#4A3206',
}
const INK: Ramp = {
  DEFAULT: '#0F1A14', muted: '#4A5A50', subtle: '#6F7C73', inverse: '#FBF8F2',
}
const SURFACE: Ramp = {
  DEFAULT: '#FBF8F2', raised: '#FFFFFF', sunken: '#F2EEE3', border: '#EAE3D2',
}
const SEMANTIC = {
  success: { 50: '#ECFDF3', 500: '#16A34A', 700: '#15803D' },
  warning: { 50: '#FFFBEB', 500: '#F59E0B', 700: '#B45309' },
  danger:  { 50: '#FEF2F2', 500: '#DC2626', 700: '#B91C1C' },
  info:    { 50: '#EFF6FF', 500: '#2563EB', 700: '#1D4ED8' },
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-surface-border pb-2">
        <h2 className="text-2xl font-semibold text-ink">{title}</h2>
        {hint ? <span className="text-sm text-ink-subtle">{hint}</span> : null}
      </div>
      {children}
    </section>
  )
}

function Swatch({ name, hex, dark }: { name: string; hex: string; dark?: boolean }) {
  return (
    <div className="rounded-lg overflow-hidden border border-surface-border bg-surface-raised shadow-card">
      <div className="h-16 w-full" style={{ backgroundColor: hex }} />
      <div className="px-3 py-2">
        <div className={`text-sm font-mono ${dark ? 'text-ink' : 'text-ink-muted'}`}>{name}</div>
        <div className="text-xs font-mono text-ink-subtle">{hex.toUpperCase()}</div>
      </div>
    </div>
  )
}

function Ramp({ label, ramp }: { label: string; ramp: Ramp }) {
  return (
    <div className="mb-6">
      <div className="mb-2 text-sm font-medium text-ink-muted">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        {Object.entries(ramp).map(([k, v]) => (
          <Swatch key={k} name={`${label.toLowerCase()}-${k}`} hex={v} />
        ))}
      </div>
    </div>
  )
}

export default function BrandPage() {
  return (
    <div className="min-h-screen bg-surface px-6 py-10">
      <div className="max-w-6xl mx-auto">
        {/* ─── Header ──────────────────────────────────────────────── */}
        <header className="mb-12">
          <div className="text-xs font-mono text-ink-subtle mb-2">/brand · design system preview</div>
          <h1 className="text-display-md text-ink">
            Brand · <span className="text-brand-700">design system</span>
          </h1>
          <p className="mt-3 max-w-2xl text-ink-muted">
            Semantic tokens for the ERP frontend. Names are brand-agnostic; values are emerald +
            warm-gold on cream. The brand identity (Injez / ARVIO / HUSBAN / TBD) can change
            without renaming a single class.
          </p>
        </header>

        {/* ─── Colors ──────────────────────────────────────────────── */}
        <Section title="Colors" hint="brand & accent are 50→900 ramps">
          <Ramp label="Brand" ramp={BRAND} />
          <Ramp label="Accent" ramp={ACCENT} />

          <div className="mb-2 text-sm font-medium text-ink-muted mt-8">Ink (text)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            {Object.entries(INK).map(([k, v]) => (
              <Swatch key={k} name={`ink-${k.toLowerCase()}`} hex={v} dark />
            ))}
          </div>

          <div className="mb-2 text-sm font-medium text-ink-muted">Surface</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            {Object.entries(SURFACE).map(([k, v]) => (
              <Swatch key={k} name={`surface-${k.toLowerCase()}`} hex={v} />
            ))}
          </div>

          <div className="mb-2 text-sm font-medium text-ink-muted">Semantic</div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {Object.entries(SEMANTIC).map(([name, ramp]) => (
              <div key={name} className="rounded-lg border border-surface-border overflow-hidden bg-surface-raised">
                <div className="grid grid-cols-3 h-12">
                  {Object.entries(ramp).map(([k, v]) => (
                    <div key={k} style={{ backgroundColor: v }} title={`${name}-${k}`} />
                  ))}
                </div>
                <div className="px-3 py-2 text-sm font-mono text-ink-muted">{name}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* ─── Typography ──────────────────────────────────────────── */}
        <Section title="Typography" hint="Inter (LTR) · Cairo (RTL)">
          <div className="space-y-4">
            <div className="flex items-baseline gap-4">
              <span className="text-display-xl text-ink">Aa</span>
              <span className="text-sm font-mono text-ink-subtle">text-display-xl · 60/66</span>
            </div>
            <div className="flex items-baseline gap-4">
              <span className="text-display-lg text-ink">Aa</span>
              <span className="text-sm font-mono text-ink-subtle">text-display-lg · 48/55</span>
            </div>
            <div className="flex items-baseline gap-4">
              <span className="text-display-md text-ink">Aa</span>
              <span className="text-sm font-mono text-ink-subtle">text-display-md · 36/43</span>
            </div>
            <div className="flex items-baseline gap-4">
              <span className="text-display-sm text-ink">Aa</span>
              <span className="text-sm font-mono text-ink-subtle">text-display-sm · 30/38</span>
            </div>
            <hr className="border-surface-border" />
            <div className="flex items-baseline gap-4"><span className="text-2xl text-ink">Heading</span><span className="text-sm font-mono text-ink-subtle">text-2xl</span></div>
            <div className="flex items-baseline gap-4"><span className="text-xl text-ink">Heading</span><span className="text-sm font-mono text-ink-subtle">text-xl</span></div>
            <div className="flex items-baseline gap-4"><span className="text-lg text-ink">Heading</span><span className="text-sm font-mono text-ink-subtle">text-lg</span></div>
            <div className="flex items-baseline gap-4"><span className="text-base text-ink">Body — Aletlala builds in Abu Dhabi.</span><span className="text-sm font-mono text-ink-subtle">text-base</span></div>
            <div className="flex items-baseline gap-4"><span className="text-sm text-ink-muted">Caption · ink-muted</span><span className="text-sm font-mono text-ink-subtle">text-sm</span></div>
            <div className="flex items-baseline gap-4"><span className="text-xs text-ink-subtle">Eyebrow · ink-subtle</span><span className="text-sm font-mono text-ink-subtle">text-xs</span></div>
            <hr className="border-surface-border" />
            <div className="font-arabic text-2xl text-ink" dir="rtl">شركة العتلال للمقاولات العامة</div>
            <div className="font-mono text-sm text-ink-subtle">font-arabic · Cairo · RTL preview</div>
          </div>
        </Section>

        {/* ─── Buttons ─────────────────────────────────────────────── */}
        <Section title="Buttons" hint=".btn-primary · .btn-secondary · .btn-ghost · .btn-accent · .btn-danger">
          <div className="flex flex-wrap gap-3 mb-6">
            <button className="btn-primary"><Plus className="w-4 h-4" /> Save Tender</button>
            <button className="btn-secondary">Cancel</button>
            <button className="btn-ghost">Skip</button>
            <button className="btn-accent">Award →</button>
            <button className="btn-danger"><Trash2 className="w-4 h-4" /> Delete</button>
          </div>
          <div className="flex flex-wrap gap-3">
            <button className="btn-primary" disabled>Disabled primary</button>
            <button className="btn-secondary" disabled>Disabled secondary</button>
            <button className="btn-accent" disabled>Disabled accent</button>
          </div>
        </Section>

        {/* ─── Inputs ──────────────────────────────────────────────── */}
        <Section title="Inputs" hint=".input · .input-error">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input className="input ps-10" placeholder="amr@aletlala.com" defaultValue="amr@aletlala.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1">Search</label>
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input className="input ps-10" placeholder="Search projects…" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1">Error state</label>
              <input className="input-error" defaultValue="not-an-email" />
              <p className="mt-1 text-xs text-danger-700">Enter a valid email address.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1">Disabled</label>
              <input className="input" disabled defaultValue="locked value" />
            </div>
          </div>
        </Section>

        {/* ─── Cards ───────────────────────────────────────────────── */}
        <Section title="Cards" hint=".card · .card-pop">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card">
              <div className="text-xs font-mono text-ink-subtle mb-2">.card</div>
              <h3 className="text-lg font-semibold text-ink mb-1">Tender · ALT-2026-014</h3>
              <p className="text-sm text-ink-muted">Mixed-use development, Saadiyat Island. Submission window opens next week.</p>
              <div className="mt-3 flex gap-2">
                <span className="badge-brand">Active</span>
                <span className="badge-neutral">AED 42M</span>
              </div>
            </div>
            <div className="card-pop">
              <div className="text-xs font-mono text-ink-subtle mb-2">.card-pop</div>
              <h3 className="text-lg font-semibold text-ink mb-1">Award notification</h3>
              <p className="text-sm text-ink-muted">Promote tender to project. Once awarded, BOQ items lock and the project cashflow generates from the schedule.</p>
              <div className="mt-3"><button className="btn-accent">Confirm award</button></div>
            </div>
          </div>
        </Section>

        {/* ─── Badges ──────────────────────────────────────────────── */}
        <Section title="Badges & chips" hint="Status communication">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="badge-brand">Brand</span>
            <span className="badge-neutral">Neutral</span>
            <span className="badge-success"><CheckCircle className="w-3 h-3" /> Approved</span>
            <span className="badge-warning"><AlertTriangle className="w-3 h-3" /> Pending</span>
            <span className="badge-danger"><XCircle className="w-3 h-3" /> Rejected</span>
            <span className="badge-info"><Info className="w-3 h-3" /> Draft</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="chip">All</span>
            <span className="chip">In progress</span>
            <span className="chip">Completed</span>
            <span className="chip">Archived</span>
          </div>
        </Section>

        {/* ─── Footer ──────────────────────────────────────────────── */}
        <footer className="mt-16 pt-6 border-t border-surface-border text-sm text-ink-subtle">
          <p>
            Tokens defined in <code className="font-mono text-ink-muted">tailwind.config.js</code>.
            Component classes in <code className="font-mono text-ink-muted">src/index.css</code>.
            Full reference in <code className="font-mono text-ink-muted">docs/BRAND.md</code>.
          </p>
        </footer>
      </div>
    </div>
  )
}
